import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { StateComparisonRow, STATE_COLORS, ProductState } from '@/types/promoAttribution';
import { formatPromoNumber } from '@/utils/promoFormatNumber';

interface Props {
  data: StateComparisonRow[];
  metric: 'avg_daily_units' | 'total_units';
  states?: ProductState[];
}

// "Promo Data" (alone) is excluded by default: in the current dataset it has
// far fewer days than the other states, so its avg-daily-units bar isn't a
// statistically comparable signal. Callers that explicitly want it can pass
// `states` to override.
const DEFAULT_CHART_STATES: ProductState[] = [
  'Standard Price',
  'Promotion',
  'Promotion and Promo Data',
];

export function PromoStateComparisonChart({ data, metric, states = DEFAULT_CHART_STATES }: Props) {
  const sorted = states
    .map((s) => data.find((r) => r.state === s))
    .filter((r): r is StateComparisonRow => r != null);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={sorted} margin={{ top: 4, right: 8, bottom: 40, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="state"
          tick={{ fontSize: 11, fill: '#64748b' }}
          angle={-18}
          textAnchor="end"
          interval={0}
          tickLine={false}
          axisLine={false}
        />
        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={40} />
        <Tooltip
          formatter={(value) => {
            const n = Number(value);
            if (metric === 'avg_daily_units') return formatPromoNumber(n, 1);
            return formatPromoNumber(n, 0);
          }}
          labelStyle={{ fontWeight: 600, fontSize: 12, color: '#0f172a' }}
          contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, background: '#fff', color: '#0f172a' }}
          cursor={{ fill: '#f8fafc' }}
        />
        <Bar dataKey={metric} radius={[4, 4, 0, 0]} maxBarSize={64}>
          {sorted.map((row) => (
            <Cell key={row.state} fill={STATE_COLORS[row.state as ProductState] ?? '#94a3b8'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
